
import { MutationHooks } from '../useLevelProgression.types';
import { Lesson } from '@/models/Lesson';

/**
 * Hook to manage lesson completion functionality
 */
export const useLessonCompletion = () => {
  /**
   * Complete lesson and award XP
   */
  const completeLessonAndAwardXP = (
    userId: string | undefined,
    selectedLesson: Lesson | null,
    totalXpGained: number,
    setShowLessonComplete: React.Dispatch<React.SetStateAction<boolean>>,
    mutations: MutationHooks,
    isPerfectLesson: boolean = false,
    handleImperfectLesson?: () => void,
    updateLessonBonus?: (isPerfect: boolean) => number
  ): number => {
    if (!userId || !selectedLesson) return 0;

    console.log(`Completing lesson: ${selectedLesson.name} with ${totalXpGained} base XP earned`);
    
    // Calculate bonus XP for perfect lesson completion
    let bonusXp = 0;
    if (updateLessonBonus) {
      bonusXp = updateLessonBonus(isPerfectLesson);
      console.debug(`🏆 Perfect lesson bonus: ${bonusXp}`);
    } else if (isPerfectLesson) {
      // Default bonus if no custom calculation function is provided
      bonusXp = 10;
      console.debug(`🏆 Using default perfect lesson bonus: ${bonusXp}`);
    }
    
    // Calculate final XP with bonus
    const finalXpTotal = totalXpGained + bonusXp;
    
    // Set global variables to ensure consistent XP is shown in UI
    (window as any).__TOTAL_XP_GAINED__ = finalXpTotal;
    (window as any).__PERFECT_LESSON_BONUS__ = bonusXp;
    (window as any).__BASE_XP_GAINED__ = totalXpGained;
    
    // CRITICAL FIX: Collect all possible indicators of perfect lesson status
    const globalPerfectStatus = (window as any).__PERFECT_LESSON__ === true;
    const wasStatusDetermined = (window as any).__PERFECT_LESSON_DETERMINED__ === true;
    
    // Check localStorage for broader context
    let storagePerfectStatus = false;
    try {
      storagePerfectStatus = localStorage.getItem('perfect_lesson_status') === 'true';
      console.log(`[useLessonCompletion] Perfect status from localStorage: ${storagePerfectStatus}`);
    } catch (e) {
      console.error('Error reading localStorage:', e);
    }
    
    // IMPORTANT: A lesson is perfect if ANY reliable source says it's perfect
    // This provides redundancy in case one mechanism fails
    const finalPerfectLessonStatus = isPerfectLesson || globalPerfectStatus || storagePerfectStatus;
    
    console.log(`[useLessonCompletion] Perfect lesson detection: passed=${isPerfectLesson}, global=${globalPerfectStatus}, storage=${storagePerfectStatus}`);
    console.log(`[useLessonCompletion] Final perfect status: ${finalPerfectLessonStatus}`);
    console.log(`[useLessonCompletion] Bonus XP amount: ${bonusXp}`);
    
    // Update global flag with final determination
    (window as any).__PERFECT_LESSON__ = finalPerfectLessonStatus;
    (window as any).__PERFECT_LESSON_BONUS__ = bonusXp;
    
    console.log(`Lesson complete! Base XP: ${totalXpGained} + ${bonusXp} bonus = ${finalXpTotal} total XP`);
    
    // Update user profile with XP - now using proper typing
    if (userId && finalXpTotal > 0 && mutations.profileMutation) {
      console.log(`Updating user profile with ${finalXpTotal} XP (includes ${bonusXp} bonus)`);
      mutations.profileMutation.mutate({
        userId: userId,
        updates: { xp: finalXpTotal }
      });
    }
    
    // Log lesson completion activity
    if (userId && selectedLesson.id) {
      console.log(`Logging lesson completion activity for: ${selectedLesson.name}`);
      // IMPORTANT: Log with the final determined perfect lesson status and bonus
      console.log(`📝 [useLessonCompletion] Logging lesson completion with isPerfectLesson=${finalPerfectLessonStatus}, bonus=${bonusXp}`);
      
      mutations.activityMutation.mutate({
        userId: userId,
        type: 'lesson-completed',
        lessonId: selectedLesson.id,
        lessonName: selectedLesson.name,
        xpEarned: finalXpTotal,
        baseXpEarned: totalXpGained, // This property is now properly defined in ActivityLog
        isPerfect: finalPerfectLessonStatus, 
        isPerfectLesson: finalPerfectLessonStatus,
        perfectLessonBonus: bonusXp
      });
      
      // Clean up recycled word counters from localStorage
      try {
        const keys = Object.keys(localStorage);
        const recycledWordKeys = keys.filter(key => key.startsWith('recycled_word_'));
        console.log(`Cleaning up ${recycledWordKeys.length} recycled word counters`);
        
        recycledWordKeys.forEach(key => {
          localStorage.removeItem(key);
        });
      } catch (e) {
        console.error('Error cleaning up recycled word counters:', e);
      }
    }
    
    // Trigger lesson complete UI
    setShowLessonComplete(true);
    
    return finalXpTotal;
  };
  
  return {
    completeLessonAndAwardXP
  };
};
